// web/src/api/support/SupportAPI.ts
import axios, { AxiosInstance } from 'axios';

export interface ChangeRequestUserDetails {
  userAgent?: string;
  referrer?: string;
}

export interface CreateChangeRequestPayload {
  page_url: string;
  request_type: string;
  error_message: string;
  report_title: string;
  report_description: string;
  user_details?: ChangeRequestUserDetails;
  additional_context?: string;
}

export interface PresignedUrlParams {
  file_name: string;
  file_size: number;
}

export interface PresignedUrlData {
  ref: string;
  url: string;
}

export interface AttachFileToTaskPayload {
  task_id: number;
  ref_id: string;
}

export interface SubmitFeedbackPayload {
  request_type: 'feedback';
  feedback: string;
}

export interface SubmitDemoRequestPayload {
  request_type: 'demo';
  date_range: string;
  time_selection: string;
  topic: string;
  emails?: string[];
}

export type SubmitSupportRequestPayload =
  | SubmitFeedbackPayload
  | SubmitDemoRequestPayload;

export default class SupportAPI {
  client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  // Creates a Teamwork task from the in-app change request form.
  // Responds with { success, data: { task_id } }.
  async createChangeRequest(payload: CreateChangeRequestPayload) {
    return this.client.post('/v2/support/teamwork-task/', payload);
  }

  // Responds with { success, data: { ref, url } } — `url` is a short lived
  // Teamwork S3 URL the file has to be PUT to, `ref` identifies the pending
  // file once it is uploaded.
  async getUploadPresignedUrl(params: PresignedUrlParams) {
    return this.client.get('/v2/support/teamwork-presigned-url/', { params });
  }

  // Deliberately not using `this.client`: the presigned URL points at a
  // third-party origin, so our cookies/CSRF headers must not be attached.
  async uploadFileToPresignedUrl(url: string, file: File) {
    return axios.put(url, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'X-Amz-Acl': 'public-read',
      },
      withCredentials: false,
    });
  }

  async attachFileToTask(payload: AttachFileToTaskPayload) {
    return this.client.post('/v2/support/teamwork-task/files/', payload);
  }

  // Feedback and demo requests are emailed instead of raised as a task.
  // Responds with { success, data: { success, message } }.
  async submitSupportRequest(payload: SubmitSupportRequestPayload) {
    return this.client.post('/v2/support/submit-support-request/', payload);
  }
}
